import React, { memo, useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { TaskCard } from './TaskCard.tsx';
import { Column, Task } from './types.ts';

interface KanbanColumnProps {
  column: Column;
  onEdit: (task: Task) => void;
  newTaskId?: number | null;
  showSparkles?: boolean;
}

const KanbanColumnComponent: React.FC<KanbanColumnProps> = ({ onEdit, column, newTaskId, showSparkles }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const getColumnStyle = useMemo(() => {
    switch (column.id) {
      case 'TODO':
        return 'bg-blue-50/80 border-blue-200/60';
      case 'IN_PROGRESS':
        return 'bg-orange-50/80 border-orange-200/60';
      case 'DONE':
        return 'bg-green-50/80 border-green-200/60';
      default:
        return 'bg-gray-50/80 border-gray-200/60';
    }
  }, [column.id]);

  const getHeaderStyle = useMemo(() => {
    switch (column.id) {
      case 'TODO':
        return 'bg-blue-100/60 border-blue-200/60';
      case 'IN_PROGRESS':
        return 'bg-orange-100/60 border-orange-200/60';
      case 'DONE':
        return 'bg-green-100/60 border-green-200/60';
      default:
        return 'bg-gray-100/60 border-gray-200/60';
    }
  }, [column.id]);

  const getBadgeStyle = useMemo(() => {
    switch (column.id) {
      case 'TODO':
        return 'bg-blue-100 text-blue-700';
      case 'IN_PROGRESS':
        return 'bg-orange-100 text-orange-700';
      case 'DONE':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }, [column.id]);



  return (
    <div className={`kanban-column flex-1 min-w-[450px] max-w-none flex flex-col bg-white rounded-2xl border shadow-lg hover:shadow-xl transition-all duration-200 ${getColumnStyle}`}>
      <div className={`p-3 flex items-center justify-between border-b rounded-t-2xl ${getHeaderStyle}`}>
        <h3 className="font-semibold text-gray-900 text-lg">
        {column.title}
        </h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBadgeStyle}`}>
          {column.tasks.length}
        </span>
      </div>
      
      <div
        ref={setNodeRef}
        className={`
          flex-1 p-4 overflow-y-auto space-y-3 min-h-[200px] max-h-[550px] transition-all duration-200
          ${isOver ? 'bg-blue-50/60 ring-2 ring-blue-300/50' : 'bg-transparent'}
        `}
        style={{ 
          touchAction: 'none',
          position: 'relative',
        }}
      >
        {column.tasks.map((task, index) => (
          <TaskCard
            key={task.id.toString()}
            task={task}
            index={index}
            onEdit={onEdit}
            isNewTask={task.id === newTaskId}
            showSparkles={showSparkles && task.id === newTaskId}
          />
        ))}
        
        {column.tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <div className="text-4xl mb-2 opacity-50">📝</div>
            <p className="text-sm text-gray-500">No tasks yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const KanbanColumn = memo(KanbanColumnComponent);